// What the guest draws.
//
// Snapshots arrive about thirty times a second and the screen redraws sixty, so
// drawing the newest one twice gives visible judder on the one thing you are
// watching. This holds the last two and renders a moment in the past, between
// them — standard entity interpolation, and the reason a chase looks like a
// chase on the phone that is not simulating it.
//
// The delay is the cost: you see the host's world ~100ms late. On shared wifi
// that is smaller than the input lag of the screen you are touching.

import { COLS_GUARD } from './maze-guard.js';

export const DELAY_MS = 100;

const lerp = (a, b, t) => a + (b - a) * t;

/**
 * Interpolate one axis, EXCEPT across the tunnel.
 *
 * A character stepping off the left edge goes from x≈0 to x≈18 in one snapshot.
 * Interpolating that draws it sprinting back across the whole maze — so a jump
 * of more than half the board is treated as the teleport it is.
 */
function lerpWrapped(a, b, t, span) {
  if (span && Math.abs(b - a) > span / 2) return t < 0.5 ? a : b;
  return lerp(a, b, t);
}

export function createBuffer() {
  let prev = null;
  let next = null;

  return {
    /** `at` is a clock reading, passed in so this is testable without one. */
    push(snapshot, at) {
      if (next && at <= next.at) return;      // out of order on an unreliable channel
      prev = next;
      next = { snapshot, at };
    },

    /** The most recent snapshot, whatever the clock says — for discrete fields. */
    latest() {
      return next?.snapshot ?? null;
    },

    /**
     * A drawable view of the world as it was DELAY_MS ago.
     *
     * Returns the newest snapshot unchanged until there are two to work with,
     * so the first frame after connecting draws immediately rather than waiting
     * for a second packet.
     */
    sample(now, delayMs = DELAY_MS) {
      if (!next) return null;
      if (!prev) return next.snapshot;
      const target = now - delayMs;
      const span = next.at - prev.at;
      if (span <= 0) return next.snapshot;
      const t = Math.max(0, Math.min(1, (target - prev.at) / span));
      const cols = next.snapshot.board?.cols ?? COLS_GUARD;

      const move = (a, b) => ({
        ...b,
        x: lerpWrapped(a.x, b.x, t, cols),
        y: lerp(a.y, b.y, t),
      });

      const a = prev.snapshot;
      const b = next.snapshot;
      return {
        ...b,
        nick: move(a.nick, b.nick),
        // Positional only. Everything discrete — eaten, player, mode, the food —
        // comes from the newer snapshot, because a half-eaten ghost is not a
        // state the game has.
        rafs: b.rafs.map((raf, i) => (a.rafs[i] ? move(a.rafs[i], raf) : raf)),
      };
    },

    reset() { prev = null; next = null; },
  };
}
